// TODO: TBD use MUI validation or custom
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  useTheme,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Trans, useTranslation } from 'react-i18next';
import { InjectionSymbols, LogType, type Logger } from '@hitiq/logger';
import { di } from '../../state/inversify.config';
import { useQuerySync } from '../../state/queryParams/useQuerySync';
import { LoginQueryParamService } from './login.queryParam.service';
import { LoginPageQuery } from './login.queryParams.interface';
import { LoginDomainService } from './loginDomainService';
import { DevLogin } from './devLogin';
import { ChangePasswordOnFirstLogin } from './components/changePasswordOnFirstLogin';
import { VerifyEmail } from '../signUp/verifyEmail';
import { version } from '../../version';
import { LegalContent } from '../../components/legalLinks/legal';
import { UserNotificationType } from '../../components/userNotification/userNotificationType.enum';
import { UserNotificationContext } from '../../components/userNotification/userNotificationContext';
import { OnboardingDesktop } from './components/smart/onBoardingDesktop.smart';
import { TextInput } from '../../components/textInput/textInput';
import { ConneqtLogo } from '../../components/logo/logo';
import { PasswordTextInput } from '../../components/passwordTextInput/passwordTextInput';
import { AlertDialog } from '../../components/alertDialog/alertDialog';
import { Button } from '../../components/button/button';
import { Spinner } from '../../components/spinner/spinner';
import { SignUpWithConsent } from '../signUp/signUpWithConsent';

export function LoginForm() {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const theme = useTheme();
  const logger = di.get<Logger>(InjectionSymbols.LOGGER);

  const loginDomainService = di.get<LoginDomainService>(LoginDomainService);
  const loginQueryParamService = di.get<LoginQueryParamService>(
    LoginQueryParamService,
  );
  useQuerySync<LoginPageQuery>(loginQueryParamService);

  const user = loginDomainService.useLoggedInUserSelector();
  const { email } = loginQueryParamService.useSelectMergedQuery();
  const authErrors = loginDomainService.useAuthErrorsSelector();
  const latestAuthError = loginDomainService.userAuthLastErrorSelector();

  const { addUserNotification } = useContext(UserNotificationContext);

  const [inputs, setInputs] = useState<{ [key: string]: string }>({
    username: '',
    password: '',
    // Add more input keys and initial values as needed
  });
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationRequired, setConfirmationRequired] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState(false);
  const [showLegalContentModal, setShowLegalContentModal] = useState(false);
  const [legalType, setLegalType] = useState('');
  const [legalHeader, setLegalHeader] = useState('');
  const parentRef = useRef(null);

  useEffect(() => {
    logger.log(LogType.debug, `Auth: email changed to: ${email}`);
    if (!user && email) {
      navigator(`/?email=${email}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    document.body.style.backgroundColor = theme.palette.text.primary;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authErrors.length > 0) {
      if (authErrors.includes('User is not confirmed.')) {
        addUserNotification(
          UserNotificationType.ERROR,
          authErrors.toString(),
          UserNotificationType.ERROR,
        );
        setIsLoading(false);
        setVerifyEmail(true);
        return;
      }
      if (latestAuthError) {
        addUserNotification(
          UserNotificationType.ERROR,
          latestAuthError,
          UserNotificationType.ERROR,
        );
      }
      setIsLoading(false);
      setInputs({
        username: '',
        password: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authErrors, latestAuthError]);

  const login = async () => {
    if (!inputs.username || !inputs.password) {
      addUserNotification(
        UserNotificationType.ERROR,
        t('USERNAME_PASSWORD_REQUIRED').toString(),
        UserNotificationType.ERROR,
      );
      return;
    }

    setIsLoading(true);

    await Auth.signIn(inputs.username, inputs.password)
      .then((_user) => {
        if (_user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          setIsLoading(false);
          setConfirmationRequired(true);
        }
      })
      .catch((error) => {
        logger.log(
          LogType.error,
          `login error other than new password required: ${error.toString()}`,
        );
      })
      .finally(() => {
        dispatchLogin();
      });
  };

  const dispatchLogin = () => {
    loginDomainService.dispatchLogin({
      email: inputs.username,
      password: inputs.password,
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setInputs((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const isDevMode = () => {
    return !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
  };

  const handleLinkClick = (e: any) => {
    e.preventDefault();
    if (e.target.tagName === 'A') {
      if (e.target.getAttribute('href') === '/terms') {
        setShowLegalContentModal(true);
        setLegalType('terms');
        setLegalHeader(t('TERMS_OF_USE_TITLE').toString());
      } else if (e.target.getAttribute('href') === '/privacy') {
        setShowLegalContentModal(true);
        setLegalType('privacy');
        setLegalHeader(t('PRIVACY_POLICY_HEADER').toString());
      }
    }
  };

  return (
    <Grid
      display="flex"
      flexDirection="row"
      justifyContent="center"
      sx={{ background: theme.palette.background.default }}
    >
      <Grid
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{ height: '100vh' }}
      >
        <ConneqtLogo />
        {isDevMode() && !confirmationRequired && !verifyEmail && <DevLogin />}

        {isLoading ? (
          <Spinner open={isLoading} />
        ) : (
          <>
            {!confirmationRequired && !verifyEmail && (
              <Container maxWidth="sm">
                <Typography
                  component="h1"
                  variant="h6"
                  sx={{
                    color: theme.palette.text.primary,
                    mt: 2,
                  }}
                >
                  {t('SIGN_IN_ORG_CLINIC')}
                </Typography>
                <Box component="form" onSubmit={login}>
                  <TextInput
                    type="email"
                    label={t('EMAIL')}
                    name="username"
                    autoComplete="username"
                    autoFocus
                    fullWidth
                    required
                    onChange={handleInputChange}
                    value={inputs.username}
                  />
                  <PasswordTextInput
                    label={t('auth.PASSWORD')}
                    name="password"
                    autoComplete="current-password"
                    fullWidth
                    required
                    onChange={handleInputChange}
                    value={inputs.password}
                  />
                  <Grid textAlign="right" sx={{ mb: 1 }}>
                    <Link
                      href="/forgot-password"
                      data-testid="forgotPassword"
                      underline="none"
                    >
                      {t('FORGOT_PASSWORD')}?
                    </Link>
                    <Button
                      color="secondary"
                      testId="signIn"
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 3,
                        mb: 2,
                        textTransform: 'none',
                        minHeight: '50px',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                      }}
                      label={t('SIGN_IN')}
                      onClick={login}
                    />
                  </Grid>
                </Box>

                <Divider>{t('common.generic.GENERIC_OR')}</Divider>

                <Grid container>
                  <Grid item>
                    <Typography
                      component="h1"
                      variant="h6"
                      sx={{
                        color: theme.palette.text.primary,
                        mt: 2,
                      }}
                    >
                      {t('SIGN_IN_GUARDIAN_PLAYER')}
                    </Typography>

                    <SignUpWithConsent />
                  </Grid>
                </Grid>
              </Container>
            )}

            <Typography
              variant="subtitle2"
              color={theme.palette.text.primary}
              sx={{ position: 'fixed', bottom: 0, right: 0, margin: 1 }}
            >
              {version}
            </Typography>

            {verifyEmail && (
              <VerifyEmail email={inputs.username} password={inputs.password} />
            )}

            {confirmationRequired && (
              <ChangePasswordOnFirstLogin
                username={inputs.username}
                password={inputs.password}
              />
            )}
          </>
        )}

        <AlertDialog
          title={legalHeader}
          setOpen={setShowLegalContentModal}
          open={showLegalContentModal}
          content={<LegalContent legal={legalType} />}
        />
      </Grid>
      <Grid
        alignSelf="center"
        sx={{ width: '50%', display: { xs: 'none', sm: 'none', md: 'block' } }}
      >
        <OnboardingDesktop />
      </Grid>
    </Grid>
  );
}
